import { useState, useCallback, useEffect, useRef } from "react";
import { Grip } from "lucide-react";
import { cn } from "../../../utils/utils";
import { DevicePopup } from "./DevicePopup";
import type { Device } from "./FloorPlanUpload";

interface DeviceMarkerProps {
  device: Device;
  isEditMode: boolean;
  isSelected: boolean;
  onMove: (deviceId: string, x: number, y: number) => void;
  onMoveEnd: (deviceId: string) => void;
  onClick: () => void;
}

export function DeviceMarker({
  device,
  isEditMode,
  isSelected,
  onMove,
  onMoveEnd,
  onClick,
}: DeviceMarkerProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Use refs for callbacks to keep event listeners stable during drag
  const onMoveRef = useRef(onMove);
  onMoveRef.current = onMove;
  const onMoveEndRef = useRef(onMoveEnd);
  onMoveEndRef.current = onMoveEnd;
  const deviceIdRef = useRef(device.id);
  deviceIdRef.current = device.id;

  const getStatusClass = (status: Device["status"]) => {
    switch (status) {
      case "active":
        return "bg-gradient-to-br from-green-400 to-green-500 border-green-600 shadow-green-500/50";
      case "warning":
        return "bg-gradient-to-br from-yellow-400 to-yellow-500 border-yellow-600 shadow-yellow-500/50";
      case "critical":
        return "bg-gradient-to-br from-red-400 to-red-500 border-red-600 shadow-red-500/50";
      case "inactive":
      default:
        return "bg-gradient-to-br from-gray-400 to-gray-500 border-gray-600 shadow-gray-500/50";
    }
  };

  const getStatusRingClass = (status: Device["status"]) => {
    switch (status) {
      case "active":
        return "ring-green-500/50";
      case "warning":
        return "ring-yellow-500/50";
      case "critical":
        return "ring-red-500/50";
      case "inactive":
      default:
        return "ring-gray-500/50";
    }
  };

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!isEditMode) return;

      e.stopPropagation();
      e.preventDefault();
      setIsDragging(true);

      const container = document.querySelector(
        '[data-container="floor-plan"]',
      ) as HTMLElement;
      if (container) {
        const rect = container.getBoundingClientRect();
        setDragOffset({
          x: e.clientX - rect.left - (device.x / 100) * rect.width,
          y: e.clientY - rect.top - (device.y / 100) * rect.height,
        });
      }
    },
    [isEditMode, device.x, device.y],
  );

  // Stable handleMouseMove that reads from refs — no dependency on onMove
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const container = document.querySelector(
        '[data-container="floor-plan"]',
      ) as HTMLElement;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const newX = Math.max(
        0,
        Math.min(
          100,
          ((e.clientX - rect.left - dragOffset.x) / rect.width) * 100,
        ),
      );
      const newY = Math.max(
        0,
        Math.min(
          100,
          ((e.clientY - rect.top - dragOffset.y) / rect.height) * 100,
        ),
      );

      onMoveRef.current(deviceIdRef.current, newX, newY);
    },
    [dragOffset],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    onMoveEndRef.current(deviceIdRef.current);
  }, []);

  // Add global event listeners for dragging — only re-runs when isDragging or dragOffset changes
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!isEditMode && !isDragging) {
        onClick();
      }
    },
    [isEditMode, isDragging, onClick],
  );

  return (
    <>
      <div
        className={cn(
          "absolute z-10 group",
          isDragging ? "will-change-transform" : "transition-all duration-200",
          isSelected && "z-20",
          isDragging && "z-30",
        )}
        style={{
          left: `${device.x}%`,
          top: `${device.y}%`,
          transform: isDragging
            ? 'translate(-50%, -50%) scale(1.25) translateZ(0)'
            : isSelected
            ? 'translate(-50%, -50%) scale(1.125)'
            : 'translate(-50%, -50%)',
        }}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
        onMouseEnter={() => !isEditMode && setShowPopup(true)}
        onMouseLeave={() => !isEditMode && setShowPopup(false)}
      >
        {/* Main marker with enhanced styling */}
        <div
          className={cn(
            "relative w-5 h-5 rounded-full border-2 shadow-lg",
            isDragging ? "" : "transition-all duration-200",
            getStatusClass(device.status),
            isEditMode && "cursor-move hover:shadow-xl",
            !isEditMode && "cursor-pointer hover:scale-110 hover:shadow-xl",
            isSelected && `ring-4 ${getStatusRingClass(device.status)}`,
            isDragging && "shadow-2xl",
          )}
        >
          {/* Animated pulse ring */}
          <div
            className={cn(
              "absolute inset-0 rounded-full border-2 animate-ping opacity-20",
              getStatusClass(device.status).replace(
                "bg-gradient-to-br",
                "border",
              ),
            )}
          />

          {/* Inner content */}
          <div className="absolute inset-0 flex items-center justify-center">
            {isEditMode ? (
              <Grip className="h-3 w-3 text-white/90" />
            ) : (
              <div className="w-2 h-2 bg-white/80 rounded-full animate-pulse" />
            )}
          </div>
        </div>

        {/* Device label with enhanced styling */}
        <div
          className={cn(
            "absolute top-8 left-1/2 transform -translate-x-1/2",
            "bg-white/95 backdrop-blur-sm border border-gray-200 rounded-lg px-3 py-1.5",
            "text-xs font-semibold text-gray-900 whitespace-nowrap shadow-lg",
            "opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none",
            "group-hover:scale-105",
          )}
        >
          <div className="flex items-center gap-1.5">
            <div
              className={cn(
                "w-2 h-2 rounded-full animate-pulse",
                device.status === "active" && "bg-green-500",
                device.status === "warning" && "bg-yellow-500",
                device.status === "critical" && "bg-red-500",
                device.status === "inactive" && "bg-gray-500",
              )}
            />
            <span>{device.name}</span>
          </div>
        </div>
      </div>

      {/* Popup on hover */}
      {showPopup && !isEditMode && <DevicePopup device={device} />}
    </>
  );
}
